import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Container,
  TextField,
  Button,
  LinearProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import DonateModal from "./DonateModal";
import DonateThankyouModal from "./DonateThankyouModal";
import { useLocation, useNavigate } from "react-router-dom";

const impactLevels = [
  {
    icon: "🚪",
    title: "$25 - Helps Cover neighborhood Door-Knocking.",
    desc: "Helps a volunteer canvass one neighborhood.",
  },
  {
    icon: "📋",
    title: "$50 - Helps cover materials/handouts.",
    desc: "Puts Gary's name in front of neighbors.",
  },
  {
    icon: "📢",
    title: "$100 - Helps cover larger communities.",
    desc: "Funds a canvas reaching 50+ doors.",
  },
  {
    icon: "🎯",
    title: "$250+ - Helps cover outreach & website.",
    desc: "Covers materials for a full precinct.",
  },
];

const donationAmounts = [25, 50, 100, 250, 500, 600];
const SupportCampaign = () => {
  const [openDonateModal, setOpenDonateModal] = useState(false);
  const [openThankyouModal, setOpenThankyouModal] = useState(false);
  const [donorName, setDonorName] = useState("");
  const [selectedAmount, setSelectedAmount] = useState(100);
  const [customAmount, setCustomAmount] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [showCancelAlert, setShowCancelAlert] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("donation") === "success") {
      // Retrieve donor name and amount saved before Stripe redirect
      const savedName = localStorage.getItem("donorName") || "Friend";
      const savedAmount = localStorage.getItem("donorAmount");
      setDonorName(savedName);
      if (savedAmount) setSelectedAmount(Number(savedAmount));
      setSessionId(params.get("session_id"));
      localStorage.removeItem("donorName");
      localStorage.removeItem("donorAmount");
      setOpenThankyouModal(true);
      // Clean up the URL to prevent showing modal on every refresh
      navigate("/", { replace: true });
    } else if (params.get("donation") === "cancel") {
      // User cancelled on Stripe checkout
      localStorage.removeItem("donorName");
      localStorage.removeItem("donorAmount");
      setShowCancelAlert(true);
      navigate("/", { replace: true });
    }
  }, [location, navigate]);

  const handleDonateSuccess = (name) => {
    setOpenDonateModal(false);
    setDonorName(name);
    setOpenThankyouModal(true);
  };

  return (
    <Box
      id="donate"
      className="support-section-root"
      sx={{ bgcolor: "#FFF", py: { xs: 6, md: 8 }, width: "100%" }}
    >
      <Container maxWidth="lg" className="support-container">
        <Box
          className="support-flex-wrapper"
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 4, md: 8 },
            alignItems: "flex-start",
          }}
        >
          {/* LEFT COLUMN: IMPACT TEXT - Scaled down */}
          <Box className="impact-content-column" sx={{ flex: 1 }}>
            <Typography
              className="section-overline"
              sx={{
                color: "#C59324",
                fontWeight: 700,
                letterSpacing: "0.2em",
                fontSize: "0.85rem",
                textTransform: "uppercase",
                mb: 2,
              }}
            >
              Support the Campaign
            </Typography>

            <Typography
              className="headline-main"
              sx={{
                fontSize: { xs: "1.6rem", sm: "2rem", md: "3rem" },
                color: "#051328",
                fontWeight: 600,
                lineHeight: 1.1,
                mb: 2,
              }}
            >
              Help{" "}
              <Box
                component="span"
                sx={{
                  color: "#C59324",
                  fontFamily: "'Playfair Display', serif",
                  fontStyle: "italic",
                  fontWeight: 500,
                }}
              >
                Make It Make Sense
              </Box>
            </Typography>

            <Typography
              className="support-description"
              sx={{
                color: "#5D6785",
                fontSize: "0.95rem",
                lineHeight: 1.6,
                mb: 4,
                maxWidth: "450px",
              }}
            >
              Every dollar goes directly to reaching voters in Middletown -
              door-to-door canvassing, events, and yard signs.
            </Typography>

            {/* IMPACT LIST - Tighter spacing */}
            <Box
              className="impact-list-stack"
              sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
            >
              {impactLevels.map((item, index) => (
                <Box
                  key={index}
                  sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}
                >
                  <Typography sx={{ fontSize: "1.2rem" }}>
                    {item.icon}
                  </Typography>
                  <Box
                    sx={{
                      borderBottom: "1px solid rgba(0,0,0,0.05)",
                      pb: 1.5,
                      width: "100%",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#051328",
                        fontWeight: 800,
                        fontSize: "0.85rem",
                        mb: 0.2,
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography sx={{ color: "#5D6785", fontSize: "0.8rem" }}>
                      {item.desc}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          {/* RIGHT COLUMN: DONATION CARD - More compact */}
          <Box
            className="donation-card-column"
            sx={{ flex: 0.8, width: "100%" }}
          >
            <Box
              className="donation-card-container"
              sx={{
                bgcolor: "#FFF",
                border: "1px solid rgba(0,0,0,0.08)",
                borderRadius: "8px",
                p: { xs: 3, md: 4 },
                boxShadow: "0px 8px 30px rgba(0,0,0,0.02)",
              }}
            >
              <Typography
                sx={{
                  color: "#C59324",
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  textAlign: "center",
                  mb: 2,
                }}
              >
                ♥ Make a Contribution
              </Typography>

              {/* PROGRESS BAR */}
              <Box className="progress-section" sx={{ mb: 3 }}>
                <LinearProgress
                  variant="determinate"
                  value={0}
                  sx={{
                    height: 4,
                    borderRadius: 2,
                    bgcolor: "#F0F0F0",
                    "& .MuiLinearProgress-bar": { bgcolor: "#C59324" },
                  }}
                />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      color: "#051328",
                      fontWeight: 700,
                    }}
                  >
                    $0{" "}
                    <Box
                      component="span"
                      sx={{ fontWeight: 400, opacity: 0.6 }}
                    >
                      raised of $10,000
                    </Box>
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      color: "#051328",
                      fontWeight: 700,
                    }}
                  >
                    0%
                  </Typography>
                </Box>
              </Box>

              {/* AMOUNT GRID */}
              <Box
                className="amount-grid"
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)" },
                  gap: 1,
                  mb: 2,
                }}
              >
                {donationAmounts.map((amt) => (
                  <Button
                    key={amt}
                    variant="outlined"
                    onClick={() => setSelectedAmount(amt)}
                    sx={{
                      py: { xs: 0.75, sm: 1 },
                      fontSize: { xs: "0.85rem", sm: "0.95rem" },
                      fontWeight: 800,
                      color: amt === selectedAmount ? "#FFF" : "#051328",
                      bgcolor:
                        amt === selectedAmount ? "#051328" : "transparent",
                      borderColor: "rgba(0,0,0,0.1)",
                      "&:hover": {
                        borderColor: "#C59324",
                        bgcolor:
                          amt === selectedAmount
                            ? "#0A1F3D"
                            : "rgba(0,0,0,0.02)",
                      },
                    }}
                  >
                    ${amt}
                  </Button>
                ))}
              </Box>

              <TextField
                fullWidth
                size="small"
                placeholder="$ Other amount (max $600)"
                variant="outlined"
                value={customAmount}
                inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                onChange={(e) => {
                  const cleaned = e.target.value.replace(/\D/g, "");
                  setCustomAmount(cleaned);
                  const val = parseInt(cleaned);
                  if (!isNaN(val) && val >= 1) {
                    setSelectedAmount(val);
                  }
                }}
                helperText={
                  selectedAmount > 600
                    ? "Delaware law limits individual donations to $600/cycle"
                    : ""
                }
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    fontSize: "0.85rem",
                    "& fieldset": {
                      borderColor: selectedAmount > 600 ? "#B8860B" : "rgba(0,0,0,0.1)",
                    },
                  },
                  "& .MuiFormHelperText-root": {
                    color: "#B8860B",
                  },
                }}
              />

              <Button
                fullWidth
                className="btn-donate"
                variant="contained"
                disabled={selectedAmount > 600}
                onClick={() => setOpenDonateModal(true)}
                sx={{
                  bgcolor: "#C59324",
                  color: "#FFF",
                  fontWeight: 700,
                  py: 1.5,
                  fontSize: "0.9rem",
                  textTransform: "none",
                  mb: 2,
                  "&:hover": { bgcolor: "#A67C1E" },
                }}
              >
                💳 Donate ${selectedAmount}
              </Button>
              <Typography
                className="disclaimer-text"
                sx={{
                  color: "#5D6785",
                  fontSize: "0.6rem",
                  textAlign: "center",
                  lineHeight: 1.5,
                  maxWidth: "300px",
                  mx: "auto",
                }}
              >
                🔒 Secure online donations powered by Stripe · Contributions
                subject to Delaware campaign finance laws.
              </Typography>
              <Typography
                className="disclaimer-text"
                sx={{
                  color: "#5D6785",
                  fontSize: "0.6rem",
                  textAlign: "center",
                  lineHeight: 1.5,
                  maxWidth: "300px",
                  mx: "auto",
                }}
              >
                Individual limit: $600/cycle. Not tax deductible.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Container>
      <DonateModal
        open={openDonateModal}
        handleClose={() => setOpenDonateModal(false)}
        onSuccess={handleDonateSuccess}
        amount={selectedAmount}
      />
      <DonateThankyouModal
        open={openThankyouModal}
        handleClose={() => {
          setOpenThankyouModal(false);
          setSessionId(null);
        }}
        donorName={donorName}
        amount={selectedAmount}
        sessionId={sessionId}
      />
      <Snackbar
        open={showCancelAlert}
        autoHideDuration={6000}
        onClose={() => setShowCancelAlert(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowCancelAlert(false)}
          severity="info"
          variant="filled"
          sx={{ width: "100%" }}
        >
          Your donation was cancelled. You can try again anytime.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SupportCampaign;
